import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于韩漫屋',
  description: '韩漫屋是一个专注于韩国漫画的免费在线阅读平台，提供高清韩漫资源，支持下拉式阅读，致力于为广大韩漫爱好者打造舒适的阅读环境。',
  url: 'https://hanmanwu.com/about',
};

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫屋',
  url: 'https://hanmanwu.com',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@hanmanwu.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
    contactOption: 'TollFree',
  },
};

const faqs = [
  {
    question: '韩漫屋的漫画资源来自哪里？',
    answer: '韩漫屋致力于为用户提供优质的韩国漫画在线阅读体验。我们的内容经过精心筛选和整理，确保每一部漫画都具有良好的阅读价值和画面品质。我们严格遵守版权规定，为用户提供合法合规的漫画阅读服务。',
  },
  {
    question: '韩漫屋是否需要注册账号？',
    answer: '韩漫屋目前不需要注册账号即可畅享所有漫画内容。我们追求简洁便捷的阅读体验，让每一位用户都能无障碍地享受韩国漫画的精彩。',
  },
  {
    question: '韩漫屋支持哪些设备阅读？',
    answer: '韩漫屋采用响应式网页设计，完美适配电脑、平板和手机等各类设备。无论您使用哪种设备，都能获得流畅舒适的下拉式阅读体验。',
  },
];

const testimonials = [
  {
    name: '小樱',
    role: '韩漫爱好者',
    content: '韩漫屋是我每天必刷的漫画网站！更新速度很快，画质也很清晰，下拉式阅读非常流畅。推荐给所有喜欢韩国漫画的朋友！',
    rating: 5,
  },
  {
    name: '阿杰',
    role: '二次元老粉',
    content: '用了半年多了，韩漫屋的漫画分类很全，找漫画特别方便。免费阅读这一点真的很良心，希望越做越好！',
    rating: 5,
  },
  {
    name: '小鹿',
    role: '漫画博主',
    content: '作为一名漫画博主，我经常在韩漫屋找素材。这里的韩漫资源丰富，更新及时，阅读体验也很棒。强力推荐！',
    rating: 5,
  },
];

export default function AboutPage() {
  return (
    <div className="pt-16">
      <PageSEO
        title="关于我们"
        description="韩漫屋 - 免费韩漫在线阅读平台。了解韩漫屋的品牌故事、用户口碑与联系方式。我们致力于提供高清韩国漫画资源与舒适阅读体验。"
        keywords="韩漫屋,关于韩漫屋,免费韩漫,韩国漫画,联系我们"
        canonicalUrl="https://hanmanwu.com/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
          关于韩漫屋
        </h1>
        <p className="text-sm text-foreground-500 mb-8">了解我们的品牌故事与使命</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-14">
          <div className="lg:col-span-3">
            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  韩漫屋诞生于一群韩国漫画爱好者的共同梦想。我们发现，虽然韩国漫画在全球范围内越来越受欢迎，但中文用户获取优质<strong>韩漫</strong>资源的渠道仍然有限。于是，我们决定打造一个专注于<strong>韩国漫画</strong>的<strong>免费在线阅读</strong>平台。
                </p>
                <p>
                  从最初的几十部漫画到如今的海量资源库，韩漫屋始终坚持<strong>免费韩漫</strong>的初心。我们采用<strong>下拉式阅读</strong>模式，让读者可以无缝滚动浏览漫画内容，无需翻页等待。同时，我们确保所有漫画均为<strong>高清</strong>画质，让每一个细节都清晰可见。
                </p>
                <p>
                  我们的编辑团队每天精心筛选最新、最热门的<strong>韩国漫画</strong>资源，涵盖恋爱、奇幻、动作、耽美、剧情、悬疑、搞笑、日常等多种分类。无论你喜欢什么类型的<strong>韩漫</strong>，在<strong>韩漫屋</strong>都能找到心仪的作品。
                </p>
                <p>
                  我们相信，优秀的漫画应该被更多人看到。<strong>韩漫屋</strong>的目标不仅是提供一个<strong>高清漫画大全</strong>，更是要<strong>营造舒适阅读环境</strong>，让每一位读者都能在这里享受漫画带来的快乐与感动。
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70 h-full">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                我们的优势
              </h2>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">完全免费阅读</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">无需付费订阅，所有漫画免费看</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-accent-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">高清画质保障</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">所有漫画均为高清画质呈现</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-secondary-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">下拉式流畅阅读</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">无缝滚动阅读，极致流畅体验</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">每日海量更新</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">热门漫画每日持续更新中</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-accent-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">全设备适配</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">手机、平板、电脑完美适配</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-2">
            用户口碑
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">听听<strong>韩漫屋</strong>用户怎么说</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70"
              >
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: t.rating }, (_, j) => (
                    <i key={j} className="ri-star-fill text-accent-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-2">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            如果您有任何问题或建议，欢迎随时与我们取得联系
          </p>
          <div className="max-w-2xl mx-auto bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@hanmanwu.com</p>
                  <p className="text-xs text-foreground-400 mt-1">24小时内回复</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">在线客服</h3>
                  <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                  <p className="text-xs text-foreground-400 mt-1">节假日休息</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-answer-line text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">内容合作</h3>
                  <p className="text-sm text-foreground-500">partner@hanmanwu.com</p>
                  <p className="text-xs text-foreground-400 mt-1">商务合作咨询</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-bug-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">问题反馈</h3>
                  <p className="text-sm text-foreground-500">feedback@hanmanwu.com</p>
                  <p className="text-xs text-foreground-400 mt-1">我们会尽快处理</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
            常见问题
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <details
                key={i}
                className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                  <span className="text-sm font-medium text-foreground-800">{faq.question}</span>
                  <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </section>
    </div>
  );
}